import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import CheckmarkFilled32 from '@carbon/icons-react/es/checkmark--filled/32';
import Login32 from '@carbon/icons-react/es/login/32';
import MagicWandFilled32 from '@carbon/icons-react/es/magic-wand--filled/32';
import { OktaContext } from '@okta/okta-react';
import { Navigate, useLocationState } from '@rocketlane/rocket-router';
import { useMutation } from '@tanstack/react-query';
import { useBoolean } from 'ahooks';
import { Result as AntResult } from 'antd';
import { useState, useContext } from 'react';
import styled from 'styled-components';
import { UserAPI } from 'api/user';
import { IconShell } from 'components/atoms/icon-shell';
import { Button } from 'components/facade/button';
import { Input as InputAtom } from 'components/facade/input';
import { Page as PageAtom } from 'components/molecules/page';
import { ProductiveHeading04, ProductiveHeading05 } from 'components/styles';
import { RoutePaths } from 'constant';
import { useCustomAuth } from 'hooks/use-custom-auth';
import { useTranslation } from 'hooks/use-translation';
const Wrapper = styled.div`
  display: flex;
  align-items: center;
`;
const Page = styled(PageAtom)`
  padding: 48px;
`;
const Input = styled(InputAtom)`
  width: 360px;
  max-width: 360px;
`;
const Email = styled.strong`
  ${ProductiveHeading04};
`;
const Result = styled(AntResult)`
  & > * {
    text-align: left;
  }
`;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: var(--layout-05);
`;
const Title = styled.div`
  ${ProductiveHeading05};
`;
export var MagicLinkMessageType = /*#__PURE__*/ function(MagicLinkMessageType) {
    MagicLinkMessageType["NotSignedIn"] = "not-signed";
    MagicLinkMessageType["Expired"] = "expired";
    return MagicLinkMessageType;
}({});
const GenerateNotifyMessage = ()=>/*#__PURE__*/ _jsx(Container, {
        children: /*#__PURE__*/ _jsx(Title, {
            children: useTranslation('magic_link.generate.success.help')
        })
    });
const GenerateLinkForm = ({ onSubmit, isLoading })=>{
    const [email, setEmail] = useState('');
    return /*#__PURE__*/ _jsxs(Wrapper, {
        children: [
            /*#__PURE__*/ _jsx(Input, {
                disabled: isLoading,
                value: email,
                onChange: (e)=>setEmail(e.target.value),
                placeholder: useTranslation('magic_link.email.placeholder'),
                onPressEnter: (e)=>onSubmit(e.currentTarget.value)
            }),
            /*#__PURE__*/ _jsx(Button, {
                loading: isLoading,
                type: "primary",
                icon: /*#__PURE__*/ _jsx(ArrowRight16, {}),
                iconPlacement: "right",
                onClick: ()=>onSubmit(email),
                children: useTranslation('magic_link.invite')
            }, "new-magic-link")
        ]
    });
};
export const MagicLinkGeneratePage = ()=>{
    var _ref;
    const state = useLocationState();
    const messageType = state ? state.messageType : null;
    const [email, setEmail] = useState('');
    const request = (_email)=>UserAPI.getMagicLink(_email);
    const [isSuccess, { setTrue }] = useBoolean(false);
    const { isLoading: loading, mutateAsync: run } = useMutation(request, {
        onSuccess: ()=>{
            setTrue();
        }
    });
    const oktaContext = useContext(OktaContext);
    const { isAuthenticated: customIsAuthenticated } = useCustomAuth();
    const authState = (_ref = oktaContext === null || oktaContext === void 0 ? void 0 : oktaContext.authState) !== null && _ref !== void 0 ? _ref : {
        isAuthenticated: customIsAuthenticated
    };
    const expiredTitle = useTranslation('magic_link.expired.title');
    const notSignedInTitle = useTranslation('magic_link.not_signed_in.title');
    const message = useTranslation('magic_link.expired.message');
    const successTitle = useTranslation('magic_link.generate.success.title');
    return !(authState === null || authState === void 0 ? void 0 : authState.isAuthenticated) ? /*#__PURE__*/ _jsx(Page, {
        children: isSuccess ? /*#__PURE__*/ _jsx(Result, {
            icon: /*#__PURE__*/ _jsx(IconShell, {
                icon: /*#__PURE__*/ _jsx(CheckmarkFilled32, {
                    color: "var(--success-color)"
                })
            }),
            title: successTitle,
            subTitle: /*#__PURE__*/ _jsx(Email, {
                children: email
            }),
            extra: [
                /*#__PURE__*/ _jsx(GenerateNotifyMessage, {}, 1)
            ]
        }) : /*#__PURE__*/ _jsx(Result, {
            icon: /*#__PURE__*/ _jsx(IconShell, {
                icon: messageType === "not-signed" ? /*#__PURE__*/ _jsx(Login32, {}) : /*#__PURE__*/ _jsx(MagicWandFilled32, {})
            }),
            title: messageType === "not-signed" ? notSignedInTitle : expiredTitle,
            subTitle: message,
            extra: [
                /*#__PURE__*/ _jsx(GenerateLinkForm, {
                    isLoading: loading,
                    onSubmit: (_email)=>{
                        setEmail(_email);
                        return run(_email);
                    }
                }, 1)
            ]
        })
    }) : /*#__PURE__*/ _jsx(Navigate, {
        replace: true,
        to: RoutePaths.home
    });
};
